import { useEffect, useRef, useState } from "react";
import styles from "./Menu.module.css";
import { Button } from "./Button.js";
import { cx } from "./cx.js";

export interface MenuAction {
  label: string;
  onSelect: () => void;
  /** Draws a rule above the item, to hold it apart from the everyday ones. */
  separated?: boolean;
  tone?: "danger";
}

interface MenuProps {
  /** Names this menu among the others on the page — one card has one of these. */
  label: string;
  actions: MenuAction[];
  trigger?: string;
  size?: "sm" | "base";
}

/**
 * The actions a thing has, behind one button.
 *
 * A card that offers everything at once reads as a wall of identical
 * buttons and hides the one action that is actually taken daily. So the
 * everyday action stays on the card and the rest live here, in the order
 * they are reached for.
 */
export function Menu({ label, actions, trigger = "More", size = "sm" }: MenuProps) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const items = useRef<Array<HTMLButtonElement | null>>([]);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) button.current?.focus();
  };

  useEffect(() => {
    if (!open) return;

    items.current[0]?.focus();

    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    /* Tab closes as well as Escape: focus leaving a menu that stays open
       leaves it hanging over content it no longer belongs to. */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close(true);
      if (event.key === "Tab") setOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const step = (from: number, by: number) => {
    const next = (from + by + actions.length) % actions.length;
    items.current[next]?.focus();
  };

  if (actions.length === 0) return null;

  return (
    <div className={styles.wrapper} ref={wrapper}>
      <Button
        ref={button}
        size={size}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown") return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        {trigger}
        <span className={styles.chevron} aria-hidden="true">
          ▾
        </span>
      </Button>

      {open && (
        <div role="menu" aria-label={label} className={styles.menu}>
          {actions.map((action, index) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              ref={(node) => {
                items.current[index] = node;
              }}
              className={cx(
                styles.item,
                action.separated && styles.separated,
                action.tone === "danger" && styles.danger,
              )}
              onClick={() => {
                close(false);
                action.onSelect();
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") step(index, 1);
                else if (event.key === "ArrowUp") step(index, -1);
                else if (event.key === "Home") items.current[0]?.focus();
                else if (event.key === "End") items.current[actions.length - 1]?.focus();
                else return;
                event.preventDefault();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
